// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

// audioOutput abstracts in-call audio-output selection across the two platforms:
//   • web/desktop → HTMLMediaElement.setSinkId, listing audiooutput devices;
//   • native Android → the capacitor-audio-route plugin (AudioManager routes:
//     earpiece / speaker / Bluetooth / wired), since setSinkId can't reach the phone's
//     physical outputs from a WebView.
// Both are presented to the UI as a flat {id,label} list + a current id.
import { isNativeApp } from "./server";
import { enumerateOutputs, outputLabel } from "./devices";

export interface AudioOutput {
  id: string; // web: deviceId · native: route ("speaker"/"earpiece"/"bluetooth"/"wired")
  label: string;
}

// A friendly label + icon hint per native route.
const ROUTE_LABEL: Record<string, string> = {
  earpiece: "Phone",
  speaker: "Speaker",
  bluetooth: "Bluetooth",
  wired: "Headphones",
};

interface AudioRoutePlugin {
  listOutputs(): Promise<{ outputs: { route: string; name: string }[]; current: string | null }>;
  setOutput(o: { route: string }): Promise<void>;
  reset(): Promise<void>;
}
async function plugin(): Promise<AudioRoutePlugin | null> {
  try {
    return (await import("capacitor-audio-route")).AudioRoute as unknown as AudioRoutePlugin;
  } catch {
    return null;
  }
}

// listAudioOutputs returns the selectable outputs + the current one (best-effort).
export async function listAudioOutputs(): Promise<{ outputs: AudioOutput[]; current: string | null }> {
  if (isNativeApp()) {
    const p = await plugin();
    if (!p) return { outputs: [], current: null };
    try {
      const { outputs, current } = await p.listOutputs();
      return { outputs: outputs.map((o) => ({ id: o.route, label: ROUTE_LABEL[o.route] ?? o.name })), current };
    } catch {
      return { outputs: [], current: null };
    }
  }
  const devs = await enumerateOutputs();
  return { outputs: devs.map((d, i) => ({ id: d.deviceId, label: outputLabel(d, i) })), current: null };
}

// selectAudioOutput routes audio to the chosen output. On web it applies setSinkId to the
// given media element(s) (the elements that actually play the call audio); on native it
// drives the OS route. Returns false if selection isn't supported.
export async function selectAudioOutput(id: string, els: (HTMLMediaElement | null)[] = []): Promise<boolean> {
  if (isNativeApp()) {
    const p = await plugin();
    if (!p) return false;
    try {
      await p.setOutput({ route: id });
      return true;
    } catch {
      return false;
    }
  }
  let ok = false;
  for (const el of els) {
    const sinkEl = el as (HTMLMediaElement & { setSinkId?: (id: string) => Promise<void> }) | null;
    if (!sinkEl?.setSinkId) continue;
    try {
      await sinkEl.setSinkId(id);
      ok = true;
    } catch {
      /* device gone or not permitted */
    }
  }
  return ok;
}

// resetAudioOutput restores normal audio mode on native after a call (no-op on web).
export async function resetAudioOutput(): Promise<void> {
  if (!isNativeApp()) return;
  const p = await plugin();
  await p?.reset().catch(() => undefined);
}

// supportsOutputSelection reports whether output switching is available at all (web
// Safari/Firefox may lack setSinkId).
export function supportsOutputSelection(): boolean {
  if (isNativeApp()) return true;
  return typeof HTMLMediaElement !== "undefined" && "setSinkId" in HTMLMediaElement.prototype;
}
